/**
 * ErrorBoundary — catches React render errors and shows recovery UI.
 *
 * Default fallback shows an EmptyState with error info and a "Try Again"
 * button. In __DEV__ mode, shows a collapsible stack trace.
 * Integrates with Sentry via onError callback.
 *
 * @see docs/milestones/M0.6-UI-PRIMITIVES.md §21
 */

import React, { Component, type ErrorInfo, type ReactNode } from 'react'
import { View, Text } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { router } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from '@/components/ui/icons'
import { Button } from './Button'
import { useTheme } from '../../lib/hooks/use-theme'
import { useSemanticColors, RADIUS, SPACING } from '../../lib/theme/tokens'
import { FONTS } from '../../lib/brand'

/**
 * Mono line box in the dev stack card, and how many of those lines the card
 * shows. `maxHeight: 200` used to cut the last row through the middle of its
 * glyphs, so the card read as damaged rather than as "there is more below".
 * The height is a whole multiple of the line box, so the clip lands in the
 * gap between two lines.
 */
const STACK_LINE_HEIGHT = 18
const STACK_VISIBLE_LINES = 10

// ─── Types ──────────────────────────────────────────────────────────────────

interface ErrorBoundaryProps {
  /** Custom fallback UI */
  fallback?: (props: { error: Error; reset: () => void }) => ReactNode
  /** Called when error is caught (for Sentry logging) */
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

// ─── Default Fallback ───────────────────────────────────────────────────────

function DefaultFallback({ error, reset }: { error: Error; reset: () => void }) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  // The raw exception is a debugging aid, not an explanation. Printed in
  // production it handed a renter "LinkifyIt.default is not a constructor",
  // centred in its own card as if it were the reason — an internal symbol
  // name that tells them nothing about what they were opening or what to do.
  // It now lives behind __DEV__ with the stack it belongs to; what ships is
  // the title + description, which are written for a person.
  const debugMessage = __DEV__ ? (error.message ?? '').slice(0, 200) : ''

  // A full-screen failure state needs a visible exit, and "Reintentar" is not
  // one — it re-runs the import that just threw and lands the user right back
  // here. `back()` is only offered when there is somewhere to go: at the root
  // boundary the navigator is unmounted along with the tree that threw, and a
  // dead control is worse than no control.
  const canLeave = (() => {
    try {
      return router.canGoBack()
    } catch {
      return false
    }
  })()

  const handleLeave = () => {
    // Navigate first. `reset` only schedules a re-render, so the route change
    // lands before the children remount — otherwise they remount straight
    // back onto the screen that threw.
    try {
      router.back()
    } catch {
      // Router not mounted (root boundary) — resetting is all that is left.
    }
    reset()
  }

  return (
    <View
      accessibilityRole="alert"
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACING['2xl'],
        backgroundColor: colors.background,
      }}
    >
      <AlertTriangle size={48} color={colors.textError} strokeWidth={1.5} />
      <Text
        style={{
          fontSize: 20,
          color: colors.textPrimary,
          fontFamily: FONTS.bodyBold,
          textAlign: 'center',
          marginTop: SPACING.md,
          marginBottom: SPACING.xs,
        }}
      >
        {t('error.boundary_title')}
      </Text>
      <Text
        style={{
          fontSize: 15,
          fontFamily: FONTS.body,
          color: colors.textSecondary,
          textAlign: 'center',
          lineHeight: 22,
          maxWidth: 280,
          marginBottom: SPACING.lg,
        }}
      >
        {t('error.boundary_desc')}
      </Text>
      {debugMessage ? (
        <Text
          selectable
          style={{
            fontSize: 12,
            lineHeight: STACK_LINE_HEIGHT,
            fontFamily: FONTS.mono,
            color: colors.textMuted,
            textAlign: 'left',
            width: '100%',
            marginBottom: SPACING.lg,
            paddingHorizontal: SPACING.md,
            paddingVertical: SPACING.sm,
            backgroundColor: colors.surface,
            borderRadius: RADIUS.sm,
          }}
        >
          {debugMessage}
        </Text>
      ) : null}
      <View style={{ alignItems: 'center', gap: SPACING.sm }}>
        <Button title={t('error.boundary_retry')} onPress={reset} variant="primary" size="md" />
        {canLeave ? (
          <Button
            title={t('common.back')}
            onPress={handleLeave}
            variant="secondary"
            size="md"
            testID="error-boundary.back"
          />
        ) : null}
      </View>
      {__DEV__ && error.stack ? (
        <ScrollView
          style={{
            marginTop: SPACING.lg,
            maxHeight: SPACING.sm * 2 + STACK_LINE_HEIGHT * STACK_VISIBLE_LINES,
            width: '100%',
            backgroundColor: colors.surface,
            borderRadius: RADIUS.sm,
            padding: SPACING.sm,
          }}
        >
          {/* `error.stack` already opens with "Error: <message>", so printing
              the message again above it put the same sentence on screen twice
              in two different treatments. */}
          <Text
            style={{
              fontSize: 12,
              lineHeight: STACK_LINE_HEIGHT,
              fontFamily: FONTS.mono,
              color: colors.textSecondary,
            }}
          >
            {error.stack}
          </Text>
        </ScrollView>
      ) : null}
    </View>
  )
}

// ─── ErrorBoundary ──────────────────────────────────────────────────────────

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onError?.(error, errorInfo)
  }

  resetError = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          reset: this.resetError,
        })
      }
      return <DefaultFallback error={this.state.error} reset={this.resetError} />
    }

    return this.props.children
  }
}
